import { useEffect, useImperativeHandle, useRef, forwardRef } from "react";

// Lightweight canvas-based signature pad. Captures mouse + touch strokes and
// exposes clear() and toDataURL() via a ref. The canvas is resized to its
// container's current CSS width and the devicePixelRatio so lines render
// crisp on retina phones and tablets.
export const SignaturePad = forwardRef(({ value, onEnd, disabled, testid = "signature-pad" }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const getCtx = () => canvasRef.current?.getContext("2d");

  const resize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const w = parent.clientWidth;
    const h = 180;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = getCtx();
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F3226";
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, w, h);
      img.src = value;
    }
  };

  useEffect(() => {
    resize();
    const onR = () => resize();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const point = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0];
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    last.current = point(e);
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const ctx = getCtx();
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    // Deliver the compressed version to the parent so the sig state stays
    // small (vs the raw retina-sized canvas, which can be hundreds of KB).
    onEnd?.(toSmallDataURL());
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = getCtx();
    ctx.fillStyle = "#fff";
    const w = canvas.width / (window.devicePixelRatio || 1);
    const h = canvas.height / (window.devicePixelRatio || 1);
    ctx.fillRect(0, 0, w, h);
    onEnd?.("");
  };

  // Produce a small-but-readable signature image for the backend. We draw
  // the live canvas onto a fixed 560 × 168 bitmap (no DPR scaling) and export
  // as PNG. Typical result: ~5–25 KB — tiny enough to POST reliably through
  // Cloudflare/ingress even with 3 signatures in the same request.
  const toSmallDataURL = () => {
    const canvas = canvasRef.current;
    if (!canvas) return "";
    try {
      const small = document.createElement("canvas");
      small.width = 560;
      small.height = 168;
      const sctx = small.getContext("2d");
      sctx.fillStyle = "#fff";
      sctx.fillRect(0, 0, small.width, small.height);
      sctx.drawImage(canvas, 0, 0, small.width, small.height);
      return small.toDataURL("image/png");
    } catch {
      // Fall back to the raw canvas if anything goes wrong (very old browser)
      return canvas.toDataURL("image/png");
    }
  };

  useImperativeHandle(ref, () => ({ clear, toDataURL: toSmallDataURL }));

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        data-testid={testid}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        className="w-full bg-white border border-forest/20 rounded cursor-crosshair touch-none"
      />
      {!disabled && (
        <button
          type="button"
          onClick={clear}
          data-testid={`${testid}-clear`}
          className="absolute top-2 right-2 text-[11px] font-bold tracking-[0.14em] uppercase text-forest/50 hover:text-forest bg-white/90 px-2 py-1 border border-forest/15"
        >
          Clear
        </button>
      )}
    </div>
  );
});
